// src/components/flight-data/TdrTab.js
import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  isOvernightFlight,
  calculateConnectionTime,
  getConnectionCategory,
} from '../../utils/connectionUtils';
import ConnectionParametersInfo from './ConnectionParametersInfo';
import ConnectionLegend from './ConnectionLegend';

/**
 * TdrTab with:
 *  - Group-hover highlighting (hovering any row in a multi-dest flight highlights them all)
 *  - Less muted colors, similar to CombinedTab
 *  - Default (arrow) cursor
 *  - Added support for freight display
 */
function TdrTab({ tdrData, params, inboundETA }) {
  // Track which flightNumber is currently hovered
  const [hoveredFlight, setHoveredFlight] = React.useState(null);

  // Inbound flight data
  const tdrInbound = tdrData.inbound || {};

  // Process and sort the TDR outbound flights
  const sortedFlights = React.useMemo(() => {
    const processedFlights = (tdrData.outbound || []).map((flight) => {
      const isOvernight = isOvernightFlight(flight);
      const connectionTime = calculateConnectionTime(flight.etd, inboundETA, isOvernight);
      const connectionCategory = getConnectionCategory(connectionTime, params, isOvernight);
      return {
        ...flight,
        isOvernight,
        connectionTime,
        connectionCategory,
      };
    });

    // Group flights by category
    const hotAndPriorityFlights = processedFlights.filter(
      (f) => f.connectionCategory === 'hot' || f.connectionCategory === 'priority'
    );
    const normalFlights = processedFlights.filter((f) => f.connectionCategory === 'normal');
    const coldFlights = processedFlights.filter((f) => f.connectionCategory === 'cold');

    // Sort each group by departure time
    hotAndPriorityFlights.sort((a, b) => (a.etd || '').localeCompare(b.etd || ''));
    normalFlights.sort((a, b) => (a.etd || '').localeCompare(b.etd || ''));
    coldFlights.sort((a, b) => (a.etd || '').localeCompare(b.etd || ''));

    // Combine sorted groups
    return [...hotAndPriorityFlights, ...normalFlights, ...coldFlights];
  }, [tdrData.outbound, inboundETA, params]);

  // Function to format connection time
  const formatConnectionTime = (mins) => {
    if (mins == null) return '--';
    const hours = Math.floor(Math.abs(mins) / 60);
    const minutes = Math.abs(mins) % 60;
    if (mins < 0) {
      return `-${hours}h -${minutes}m`;
    }
    return `${hours}h ${minutes}m`;
  };

  /**
   * Base (non-hover) background color for each category.
   * These alphas are closer to the CombinedTab approach (less muted).
   */
  const getBaseColor = (category) => {
    if (category === 'hot')      return 'rgba(255, 0, 0, 0.4)';   // match Combined tab red
    if (category === 'priority') return 'rgba(255, 165, 0, 0.3)'; // match Combined tab orange
    if (category === 'cold')     return 'rgba(0, 0, 255, 0.2)';   // match Combined tab blue
    return 'transparent';                                         // normal flights
  };

  /**
   * Hover highlight color for each category.
   * We use slightly higher alpha so the highlight is more pronounced.
   */
  const getHoverColor = (category) => {
    if (category === 'hot')      return 'rgba(255, 0, 0, 0.6)';
    if (category === 'priority') return 'rgba(255, 165, 0, 0.5)';
    if (category === 'cold')     return 'rgba(0, 0, 255, 0.35)';
    return 'rgba(255, 255, 255, 0.1)'; // subtle highlight for "normal"
  };

  // Format freight for display
  const formatFreight = (freight) => {
    if (!freight || !Array.isArray(freight) || freight.length === 0) {
      return '';
    }
    
    return freight.map(item => {
      const type = item.type || 'NFG';
      return `${type}: ${item.count}`;
    }).join(', ');
  };

  // Render rows for a flight
  const renderFlightRows = (flight) => {
    const { flightNumber, destinations, connectionCategory } = flight;
    const hasMultipleDestinations = destinations && destinations.length > 1;

    // If hovered flight = this flightNumber, highlight all sub-rows
    const isHovered = hoveredFlight === flightNumber;
    const baseBackground = getBaseColor(connectionCategory);
    const highlightBackground = getHoverColor(connectionCategory);

    // rowSx => picks highlight if isHovered, else uses base color
    const rowSx = {
      backgroundColor: isHovered ? highlightBackground : baseBackground,
      // Keep the default arrow cursor
      cursor: 'default',
    };

    // onMouseEnter/Leave to set/unset hoveredFlight
    const rowEvents = {
      onMouseEnter: () => setHoveredFlight(flightNumber),
      onMouseLeave: () => setHoveredFlight(null),
    };

    // SINGLE-DESTINATION FLIGHT
    if (!hasMultipleDestinations) {
      return (
        <TableRow
          key={flightNumber}
          sx={{
            ...rowSx,
            '& td': {
              // optional top border if you want to separate from the previous row
              borderTop: '1px solid rgba(255, 255, 255, 0.87)',
            },
          }}
          {...rowEvents}
        >
          <TableCell sx={{ color: '#fff', fontWeight: flight.isOvernight ? 'bold' : 'medium' }}>
            {flight.flightNumber}
            {flight.isOvernight ? '' : ''}
          </TableCell>
          <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
            {flight.registration}
          </TableCell>
          <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
            {flight.gate}
          </TableCell>
          <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
            {flight.etd}
          </TableCell>
          <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
            <span style={{ color: connectionCategory === 'hot' ? '#ff6666' : '#fff' }}>
              {formatConnectionTime(flight.connectionTime)}
            </span>
          </TableCell>
          <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
            {/* Display only the clean destination name - NO HOT! indicator */}
            {flight.destination}
          </TableCell>
          <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
            {flight.bags}
          </TableCell>
          <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
            {formatFreight(flight.freight)}
          </TableCell>
        </TableRow>
      );
    }

    // MULTI-DESTINATION FLIGHT
    const rows = [];

    destinations.forEach((dest, i) => {
      if (i === 0) {
        // FIRST row => remove bottom border so there's no line after this row
        rows.push(
          <TableRow
            key={`${flightNumber}-${i}`}
            sx={{
              ...rowSx,
              '& td': {
                borderBottom: 'none',
              },
            }}
            {...rowEvents}
          >
            <TableCell
              rowSpan={destinations.length}
              sx={{
                color: '#fff',
                fontWeight: flight.isOvernight ? 'bold' : 'medium',
              }}
            >
              {flight.flightNumber}
              {flight.isOvernight ? '' : ''}
            </TableCell>
            <TableCell
              rowSpan={destinations.length}
              sx={{
                color: '#fff',
                fontWeight: 'medium',
              }}
            >
              {flight.registration}
            </TableCell>
            <TableCell
              rowSpan={destinations.length}
              sx={{
                color: '#fff',
                fontWeight: 'medium',
              }}
            >
              {flight.gate}
            </TableCell>
            <TableCell
              rowSpan={destinations.length}
              sx={{
                color: '#fff',
                fontWeight: 'medium',
              }}
            >
              {flight.etd}
            </TableCell>
            <TableCell
              rowSpan={destinations.length}
              sx={{
                color: '#fff',
                fontWeight: 'medium',
              }}
            >
              <span style={{ color: connectionCategory === 'hot' ? '#ff6666' : '#fff' }}>
                {formatConnectionTime(flight.connectionTime)}
              </span>
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
              {/* Display only the clean destination name - NO HOT! indicator */}
              {dest.city}
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
              {dest.bags}
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
              {formatFreight(dest.freight)}
            </TableCell>
          </TableRow>
        );
      } else if (i < destinations.length - 1) {
        // MIDDLE rows => also remove bottom border
        rows.push(
          <TableRow
            key={`${flightNumber}-${i}`}
            sx={{
              ...rowSx,
              '& td': {
                borderBottom: 'none',
              },
            }}
            {...rowEvents}
          >
            <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
              {/* Display only the clean destination name - NO HOT! indicator */}
              {dest.city}
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
              {dest.bags}
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
              {formatFreight(dest.freight)}
            </TableCell>
          </TableRow>
        );
      } else {
        // LAST row => normal MUI bottom border
        rows.push(
          <TableRow
            key={`${flightNumber}-${i}`}
            sx={rowSx}
            {...rowEvents}
          >
            <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
              {/* Display only the clean destination name - NO HOT! indicator */}
              {dest.city}
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
              {dest.bags}
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'medium' }}>
              {formatFreight(dest.freight)}
            </TableCell>
          </TableRow>
        );
      }
    });

    return rows;
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      <h3 style={{ color: '#F9B612' }}>TDR</h3>

      {/* Connection Parameters Info */}
      <ConnectionParametersInfo params={params} color="#F9B612" />

      {/* Inbound Flight Info */}
      <h4 style={{ color: '#F9B612' }}>Inbound Flight Info</h4>
      <TableContainer component={Paper} sx={{ marginBottom: '2rem', backgroundColor: '#1f2937' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}>Flight Num</TableCell>
              <TableCell sx={{ color: '#fff' }}>Arriving From</TableCell>
              <TableCell sx={{ color: '#fff' }}>Registry</TableCell>
              <TableCell sx={{ color: '#fff' }}>Arrival Gate</TableCell>
              <TableCell sx={{ color: '#fff' }}>ETA</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}>{tdrInbound.flightNumber}</TableCell>
              <TableCell sx={{ color: '#fff' }}>{tdrInbound.arrivingFrom}</TableCell>
              <TableCell sx={{ color: '#fff' }}>{tdrInbound.registry}</TableCell>
              <TableCell sx={{ color: '#fff' }}>{tdrInbound.arrivalGate}</TableCell>
              <TableCell sx={{ color: '#fff' }}>{tdrInbound.eta}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Outbound Flights */}
      <h4 style={{ color: '#F9B612' }}>Outbound Flights</h4>

      {/* Legend Box */}
      <ConnectionLegend params={params} />

      <TableContainer component={Paper} sx={{ marginBottom: '2rem', backgroundColor: '#1f2937' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}>Num</TableCell>
              <TableCell sx={{ color: '#fff' }}>Registration</TableCell>
              <TableCell sx={{ color: '#fff' }}>Gate</TableCell>
              <TableCell sx={{ color: '#fff' }}>ETD</TableCell>
              <TableCell sx={{ color: '#fff' }}>Connection</TableCell>
              <TableCell sx={{ color: '#fff' }}>Destination(s)</TableCell>
              <TableCell sx={{ color: '#fff' }}>Total Bags</TableCell>
              <TableCell sx={{ color: '#fff' }}>Freight</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedFlights.map((flight) => {
              // Only render if this is the first occurrence of that flightNumber
              const isFirstOfFlight =
                sortedFlights.findIndex((f) => f.flightNumber === flight.flightNumber) ===
                sortedFlights.indexOf(flight);
              if (!isFirstOfFlight) return null;

              return renderFlightRows(flight);
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default TdrTab;